import { ofType } from 'redux-observable';
import {
  AuctionActionEnum,
  IFetchAuctionDataOnlyAction,
  IFetchedAuctionDataOnlyWithoutDateAction,
  IFetchFiltersAndAuctionAction,
} from './index';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  AuctionMessageEnum,
  AuctionService,
  BidService,
  IAuction,
  IBidResultDetail,
  ICorridorDetail,
  AuctionResultService,
  IItaAuctionResult,
} from '../../services';
import {
  FetchAuctionDataOnlyFailureAction,
  FetchedAuctionDataOnlySuccessAction,
  FetchedFiltersAndAuctionDataFailedAction,
  FetchedFiltersAndAuctionDataSuccessAction,
} from './ActionCreator';
import {
  IApplicationState,
  IAuctionFilters,
} from '..';
import { EMPTY, forkJoin, of } from 'rxjs';

const auctionSerivce = new AuctionService();
const bidService = new BidService();
const auctionResultService = new AuctionResultService();

export const fetchFilterAndAuctionDataEpic = (action$: any, state$: any) =>
  action$.pipe(
    ofType(AuctionActionEnum.FetchFiltersAndAuctionData),
    withLatestFrom(state$),
    mergeMap(
      ([action, state]: [IFetchFiltersAndAuctionAction, IApplicationState]) => {
        return bidService.getAllCorridors().pipe(
          switchMap((corridors : ICorridorDetail[]) => {
            if(!action.state?.initialFilters || !state.auction.currentFilters){
              return EMPTY;
            }
            return forkJoin([
              of(corridors),
              of(action.state?.initialFilters),
              of(state.auction.currentFilters),
            ]);
          }),
          switchMap(
            (data: [ICorridorDetail[], IAuctionFilters, IAuctionFilters]) => {
              const response = auctionSerivce.getAuctions(
                data[0].map((item) => item.corridorKey),
                data[1].startDateUtc
              );
              return forkJoin([response, of(data[0]), of(data[2])]);
            }
          ),
          switchMap(
            (data: [IAuction[], ICorridorDetail[], IAuctionFilters]) => {
              const ids = data[0].map((x) => x.identification);
              const response = bidService.getBidDetailsByAuctionIds(ids);
              const auctionNames = data[0].map((x) => x.auctionName);
              const auctionResults = auctionResultService.getAuctionResults(auctionNames)
              return forkJoin([
                of(data[0]),
                of(data[1]),
                response,
                of(data[2]),
                auctionResults
              ]);
            }
          ),
          map(
            (
              response: [
                IAuction[],
                ICorridorDetail[],
                IBidResultDetail[],
                IAuctionFilters,
                IItaAuctionResult[]
              ]
            ) => {
              const auctions = response[0]
                ? auctionSerivce.assignBidResultsToAuctions(
                    response[0],
                    response[2],
                    response[3].startDateUtc
                  )
                : [];             
              const auctionWithResultStatus = auctionSerivce.assignItaAuctionResultsToAuctions(auctions, response[4]);
              return FetchedFiltersAndAuctionDataSuccessAction(
                auctionWithResultStatus,
                response[1]
              );
            }
          ),
          catchError((x) => {
            console.error(x);
            return of(
              FetchedFiltersAndAuctionDataFailedAction(
                AuctionMessageEnum.UnableToGetAuctionResult
              )
            );
          })
        );
      }
    )
  );

export const fetchAuctionDataOnlyEpic = (action$: any, state$: any) =>
  action$.pipe(
    ofType(AuctionActionEnum.FetchAuctionDataOnlyAction),
    withLatestFrom(state$),
    mergeMap(
      ([action, state]: [IFetchAuctionDataOnlyAction, IApplicationState]) => {
        const corridorCodes = state?.auction?.initialFilters?.corridorKeys;
        const dateTime = action.state?.currentFilters?.startDateUtc;

        // Guard: corridorKeys are only populated after FetchFiltersAndAuctionData
        // completes. On reload, SignalR events can fire before that, producing
        // an empty-corridorCodes API call that fails with an error banner.
        if (!corridorCodes || corridorCodes.length === 0) {
          return EMPTY;
        }

        return auctionSerivce.getAuctions(corridorCodes, dateTime).pipe(
          switchMap((auctions : IAuction[]) => {
            if(!state.auction.currentFilters){
              return EMPTY;
            }
            return forkJoin([of(auctions), of(state.auction.currentFilters)]);
          }),
          switchMap((data: [IAuction[], IAuctionFilters]) => {
            const ids = data[0].map((x) => x.identification);
            const response = bidService.getBidDetailsByAuctionIds(ids);
            const auctionNames = data[0].map((x) => x.auctionName);
            const auctionResults = auctionResultService.getAuctionResults(auctionNames)
            return forkJoin([of(data[0]), response, of(data[1]), auctionResults]);
          }),
          map((response: [IAuction[], IBidResultDetail[], IAuctionFilters, IItaAuctionResult[]]) => {
            const auctions = response[0]
              ? auctionSerivce.assignBidResultsToAuctions(
                  response[0],
                  response[1],
                  response[2].startDateUtc
                )
              : [];
            const auctionWithResultStatus = auctionSerivce.assignItaAuctionResultsToAuctions(auctions, response[3]);
            return FetchedAuctionDataOnlySuccessAction(auctionWithResultStatus);
          }),
          catchError((x) => {
            console.error(x);
            return of(
              FetchAuctionDataOnlyFailureAction(
                AuctionMessageEnum.UnableToGetAuctionResult
              )
            );
          })
        );
      }
    )
  );

  export const fetchAuctionDataOnlyWithDateEpic = (action$: any, state$: any) =>
  action$.pipe(
    ofType(AuctionActionEnum.FetchAuctionDataOnlyWithoutDateAction),
    withLatestFrom(state$),
    mergeMap(
      ([action, state]: [IFetchedAuctionDataOnlyWithoutDateAction, IApplicationState]) => {
        const corridorCodes = state?.auction?.initialFilters?.corridorKeys;
        const dateTime = state?.auction?.currentFilters?.startDateUtc;

        // Guard: skip if corridorKeys haven't been loaded yet (e.g. SignalR
        // fires before the full filter/auction fetch on reload).
        if (!corridorCodes || corridorCodes.length === 0) {
          return EMPTY;
        }
        return auctionSerivce.getAuctions(corridorCodes, dateTime).pipe(
          switchMap((auctions : IAuction[]) => {
            if(!state.auction.currentFilters){
              return EMPTY;
            }
            return forkJoin([of(auctions), of(state.auction.currentFilters)]);
          }),
          switchMap((data: [IAuction[], IAuctionFilters]) => {
            const ids = data[0].map((x) => x.identification);
            const response = bidService.getBidDetailsByAuctionIds(ids);
            const auctionNames = data[0].map((x) => x.auctionName);
            const auctionResults = auctionResultService.getAuctionResults(auctionNames)
            return forkJoin([of(data[0]), response, of(data[1]), auctionResults]);
          }),
          map((response: [IAuction[], IBidResultDetail[], IAuctionFilters, IItaAuctionResult[]]) => {
            const auctions = response[0]
              ? auctionSerivce.assignBidResultsToAuctions(
                  response[0],
                  response[1],
                  response[2].startDateUtc
                )
              : [];
            const auctionWithResultStatus = auctionSerivce.assignItaAuctionResultsToAuctions(auctions, response[3]);
            return FetchedAuctionDataOnlySuccessAction(auctionWithResultStatus);
          }),          
          catchError((x) => {
            console.error(x);
            return of(
              FetchAuctionDataOnlyFailureAction(
                AuctionMessageEnum.UnableToGetAuctionResult
              )
            );
          })
        );
      }
    )
  );
